import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import axios from 'axios';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import * as Sentry from '@sentry/react';
import { CustomerContextProvider } from '../../../contexts/CustomerContext';
import Nav from '../../Nav/v2/Nav';
import Home from '../../_pages/Home';
import StudentIdEntry from '../../_pages/StudentIdEntry';
import StudentInfoEntry from '../../_pages/StudentInfoEntry';
import PackageSelection from '../../_pages/PackageSelection';
import DownloadImages from '../../_pages/DownloadImages/DownloadImages';
import BackgroundSelection from '../../_pages/BackgroundSelection/BackgroundSelection';
import OptionSelection from '../../_pages/OptionSelection/OptionSelection';
import PreviousImages from '../../_pages/PreviousImages/v2/PreviousImages';
import OrderReview from '../../_pages/order-review';
import Checkout from '../../_pages/Checkout/Checkout';
import {
  ApptCartRequiredRoute,
  CartRequiredRoute,
  CurrentStudentRequiredRoute,
  OrderPackageRequiredRoute,
} from '../../../utils/custom-routes';
import AddonsSelection from '../../_pages/AddonsSelection/AddonsSelection';
import OrderComplete from '../../_pages/OrderComplete';
import Refocuser from '../../Refocuser';
import { routes, LEGACY_FREE_MAGNETS_PATH } from '../../../routes';
import AppointmentOptionSelection from '../../_pages/appointment-session-selection';
import AppointmentSelection from '../../_pages/appointment-selection';
import './CustomerApp.sass';
import AppointmentReview from '../../_pages/appointment-review';
import ScheduleAppointment from '../../_pages/schedule-appointment';
import RescheduleAppointment from '../../_pages/reschedule-appointment';
import FavoritePoseSelection from '../../_pages/favorite-pose-selection';
import SeniorOptionSelection from '../../_pages/senior-option-selection';
import PackageBuilder from '../../_pages/package-builder';
import SeniorAddonsPrev from '../../_pages/senior-addons';
import SeniorPortraitsReview from '../../_pages/senior-portraits-review';
import Sittings from '../../_pages/Sittings';
import ScheduleAtShoob from '../../_pages/ScheduleAtShoob';
import FrequentlyAskedQuestions from '../../_pages/FrequentlyAskedQuestions';
import ElementaryMultiPoseReview from '../../_pages/_ElementaryMultiPose/Review';
import Yearbooks from '../../_pages/_landing/Yearbooks';
import Composites from '../../_pages/_landing/Composites';
import DownloadImageForm from '../../_pages/DownloadImage/DownloadImageForm';
import DownloadImage from '../../_pages/DownloadImage';
import EarlyPurchase from '../../_pages/EarlyPurchase';
import GetAccessCode from '../../_pages/GetAccessCode/GetAccessCode';
import OrderStatus from '../../_pages/OrderStatus';
import SheetBackgroundSelection from '../../_pages/_landing/BackgroundSelection';
import TeacherSignup from '../../_pages/Signup/TeacherSignup';
import TeacherLoginCode from '../../_pages/Signup/TeacherLoginCode';
import MobileUpload from '../../_pages/GetAccessCode/MobileUpload';
import Panorama from '../../_pages/_landing/Panorama';
import PackageLanding from '../../_pages/_landing/Package/PackageLanding';
import YearbookPromo from '../../_pages/_promos/Yearbooks';
import Promos from '../../_pages/_promos/Promos';
import Registration from '../../_pages/Registration';
import ConfirmStudent from '../../_pages/ConfirmStudent';
import Cart from '../../_pages/Cart';
import MultiPosePackageBuilder from '../../_pages/MultiPosePackageBuilder';
import 'react-perfect-scrollbar/dist/css/styles.css';
import '../../PackageBuilders/MultiPose/scrollbar.scss';
import MultiPoseEdit from '../../_pages/MultiPoseEdit';
import FreeMagnets from '../../_pages/_ElementaryMultiPose/FreeMagnets';
import './visible-scrollbars.css';
import Retouching from '../../_pages/Retouching';
import Promotions from '../../_pages/Promotions';
import ScrollbarTest from '../../_pages/ScrollbarTest';
import IndividualPrintEdit from '../../_pages/IndividualPrintEdit';
import Addons from '../../_pages/Addons';
import SeniorBuilder from '../../_pages/seniors/Builder';
import SeniorAddons from '../../_pages/seniors/Addons';
import SeniorIndividualPrints from '../../_pages/seniors/IndividualPrints';
import Retakes from '../../_pages/Retakes/Retakes';
import Confirmation from '../../_pages/Retakes/Confirmation/Confirmation';
import Offer from '../../_pages/_landing/Offer';
import Holiday from '../../_pages/_landing/Holiday';
import HolidayBuilder from '../../_pages/Holiday/Builder';
import { EmailInterceptorProvider } from '../../EmailInterceptor/EmailInterceptor';
import EmailInterceptor from '../../EmailInterceptor';
import HolidayBanner from '../../HolidayBanner';
import DesktopSiteBanner from '../../DesktopSiteBanner';
import HolidayEdit from '../../_pages/Holiday/Edit';
import PastImagesBuilder from '../../_pages/PastImages/Builder/PastImagesBuilder';
import PastImagesQuickOrder from '../../_pages/PastImagesQuickOrder';
import SeniorPastImagesQuickOrder from '../../_pages/SeniorPastImagesQuickOrder';
import Confirm from '../../_pages/Confirm';
import StripeTestCheckout from '../../_pages/StripeTestCheckout/StripeTestCheckout';
import StripeTestCheckoutComplete from '../../_pages/StripeTestCheckout/Complete';
import TestReview from '../../_pages/StripeTestCheckout/TestReview';

const BASE_OPTIONS = {
  refetchOnReconnect: false,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  retry: 0,
};

const client = new QueryClient({
  defaultOptions: {
    queries: { ...BASE_OPTIONS },
  },
});

const CustomerApp = () => {
  document.querySelectorAll('.container.container-fluid').forEach((node) => node.classList.remove('container', 'container-fluid'));
  document.querySelectorAll('footer').forEach((node) => node.remove());
  document.querySelectorAll('.row').forEach((node) => node.remove());
  document.querySelectorAll('br').forEach((node) => node.remove());

  axios.defaults.baseURL = '/api/v1';

  // Sentry first: loadStripe below reports its failure through it.
  Sentry.init({
    dsn: `${process.env.SENTRY_JS_ENDPOINT}`,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: 1.0,
  });

  const stripePromise = loadStripe(process.env.STRIPE_PUBLISHABLE_KEY);

  // Stripe.js is loaded once here, at the ROOT of the customer SPA — so a failure is reported
  // against whatever page the customer happened to land on (almost always "/"), NOT against a
  // payment step. That made "Failed to load Stripe.js" look like a checkout failure when it
  // usually is not: credit card goes to ActiveMerchant server-side and PayPal has its own SDK,
  // so neither needs this. Apple/Google Pay degrades to no button.
  //
  // What the bare error could never tell us is whether the customer went on to REACH checkout
  // with a dead Stripe — the case that actually costs an order. So record where they were, and
  // tag it so the harmless boot-time failures can be separated from the ones that matter.
  stripePromise.catch((error) => {
    Sentry.captureException(error, {
      tags: {
        stripeJsLoad: 'failed',
        stripeJsLoadRoute: window.location.pathname,
      },
      extra: {
        note: 'Stripe.js failed at SPA boot. Card + PayPal are unaffected; Afterpay is hidden '
              + 'by canOfferAfterpay(). Check stripeJsLoadRoute to see if a payment step was reached.',
        href: window.location.href,
      },
    });
  });

  return (
    <Elements stripe={stripePromise}>
      <div className="customer-app">
        <CustomerContextProvider>
          <QueryClientProvider client={client}>
            <Router basename="/c/">
              <EmailInterceptorProvider>
                <EmailInterceptor />
                <Nav />
                <DesktopSiteBanner />
                <HolidayBanner />
                <Switch>
                  <Route exact path="/" component={Home} />
                  <Route path="/cart" component={OrderReview} />
                  <Route
                    path={routes.frequentlyAskedQuestions()}
                    component={FrequentlyAskedQuestions}
                  />
                  <Route
                    path={routes.downloadImage(':orderId', ':email')}
                    component={DownloadImages}
                  />
                  <Route
                    path={routes.downloadImageForm()}
                    component={DownloadImageForm}
                  />
                  <Route path="/react-scrollbar-test" component={ScrollbarTest} />
                  <Route path={routes.seniorAddons()} component={SeniorAddonsPrev} />
                  <CartRequiredRoute path={routes.elementaryMultiPoseBuilder(':packageId')} component={MultiPosePackageBuilder} />
                  <CartRequiredRoute path={routes.multiPoseEdit(':orderPackageId')} component={MultiPoseEdit} />
                  <CartRequiredRoute path={routes.freeMagnets(':orderPackageId')} component={FreeMagnets} />
                  {/* The page's old elementary URL — kept so a customer mid-flow
                      across the deploy doesn't land on a blank Switch miss. */}
                  <CartRequiredRoute path={LEGACY_FREE_MAGNETS_PATH} component={FreeMagnets} />
                  <Route path={routes.individualPrintEdit(':orderPackageId')} component={IndividualPrintEdit} />
                  <Route path="/student_id_entry" component={StudentIdEntry} />
                  <CartRequiredRoute path="/student_info_entry" component={StudentInfoEntry} />
                  <OrderPackageRequiredRoute path="/background_selection/:package_id" component={BackgroundSelection} />
                  <ApptCartRequiredRoute path={routes.appointmentOptionSelection(':packageId')} component={AppointmentOptionSelection} />
                  <ApptCartRequiredRoute
                    path={routes.appointmentSelection()}
                    component={AppointmentSelection}
                  />
                  <Route path={routes.retouching(':orderPackageId')} component={Retouching} />
                  <Route path={routes.promotions(':optionId')} component={Promotions} />
                  <Route
                    path={routes.registration()}
                    component={Registration}
                  />
                  {/* Reached only from a text or email we sent, and keyed on an
                      unguessable token rather than any login -- the parent has
                      neither an account nor an access code, which is the whole
                      situation this page exists for. */}
                  <Route
                    path={routes.confirmStudent()}
                    component={ConfirmStudent}
                  />
                  <CartRequiredRoute
                    path={routes.pastImages.quickOrder()}
                    component={PastImagesQuickOrder}
                  />
                  <CartRequiredRoute
                    path={routes.pastImages.seniorQuickOrder()}
                    component={SeniorPastImagesQuickOrder}
                  />
                  <Route
                    path={routes.pastImages.builder(':packageId')}
                    component={PastImagesBuilder}
                  />
                  <Route
                    path={routes.yearbookPromos()}
                    component={YearbookPromo}
                  />
                  <Route
                    path={routes.addons(':orderPackageId')}
                    component={Addons}
                  />
                  <Route
                    path={routes.promos(':packageId')}
                    component={Promos}
                  />
                  <CurrentStudentRequiredRoute
                    path={routes.favoritePoseSelection(':packageId')}
                    component={FavoritePoseSelection}
                  />
                  <Route
                    path={routes.sittings()}
                    component={Sittings}
                  />
                  <Route
                    path={routes.seniorOptionSelection(':packageId')}
                    component={SeniorOptionSelection}
                  />
                  <Route
                    path={routes.seniorPortraitReview(':orderPackageId')}
                    component={SeniorPortraitsReview}
                  />
                  <Route
                    path={routes.multiPoseReview(':orderPackageId')}
                    component={ElementaryMultiPoseReview}
                  />
                  <Route
                    path={routes.mobileUpload()}
                    component={MobileUpload}
                  />
                  <Route
                    path={routes.packageLanding(':packageId')}
                    component={PackageLanding}
                  />
                  <Route path={routes.panorama()} component={Panorama} />
                  <Route path={routes.earlyPurchase()} component={EarlyPurchase} />
                  <OrderPackageRequiredRoute path="/option_selection/:package_id" component={OptionSelection} />
                  <OrderPackageRequiredRoute path="/addons" component={AddonsSelection} />
                  <Route path={routes.scheduleAppointment()} component={ScheduleAppointment} />
                  <Route path={routes.rescheduleAppointment()} component={RescheduleAppointment} />
                  <ApptCartRequiredRoute path={routes.scheduleAtShoob()} component={ScheduleAtShoob} />
                  <CartRequiredRoute path="/package_selection" component={PackageSelection} />
                  <CartRequiredRoute path="/previous_images" component={PreviousImages} />
                  <Route path={routes.packageBuilder(':packageId')} component={PackageBuilder} />
                  <Route path="/past_images" component={PreviousImages} />
                  <ApptCartRequiredRoute
                    path={routes.appointmentReview()}
                    component={AppointmentReview}
                  />
                  <Route
                    path={routes.getAccessCode()}
                    component={GetAccessCode}
                  />
                  <Route path={routes.holiday.builder()} component={HolidayBuilder} />
                  <Route path={routes.holiday.edit(':orderPackageId')} component={HolidayEdit} />
                  <CartRequiredRoute path={routes.review()} component={OrderReview} />
                  <Route path="/addons" component={AddonsSelection} />
                  <Route path="/order/complete/:order_id/:email" component={OrderComplete} />
                  <Route path="/order/download_images/:order_id/:email" component={DownloadImages} />
                  <Route path={routes.checkout()} component={Checkout} />
                  <Route path={routes.yearbooks()} component={Yearbooks} />
                  <Route path={routes.composites()} component={Composites} />
                  <Route path={routes.orderStatus()} component={OrderStatus} />
                  <Route
                    path={routes.sheetBackgroundSelection(':orderId')}
                    component={SheetBackgroundSelection}
                  />
                  <Route
                    path={routes.confirm(':orderPackageId')}
                    component={Confirm}
                  />
                  <Route path={routes.testCheckoutComplete()} component={StripeTestCheckoutComplete} />
                  <Route path={routes.testReview()} component={TestReview} />
                  <Route path={routes.testCheckout()} component={StripeTestCheckout} />
                  <Route path={routes.teacherSignup()} component={TeacherSignup} />
                  <Route path={routes.teacherLoginCode()} component={TeacherLoginCode} />
                  <Route path={routes.seniors.packageBuilder(':packageId')} component={SeniorBuilder} />
                  <Route path={routes.seniors.addons(':orderPackageId')} component={SeniorAddons} />
                  <Route path={routes.seniors.individualPrintsEdit(':orderPackageId')} component={SeniorIndividualPrints} />
                  <Route path={routes.retakes.confirmation()} component={Confirmation} />
                  <Route path={routes.retakes.home()} component={Retakes} />
                  <Route path={routes.landing.offer()} component={Offer} />
                  <Route path={routes.landing.holiday()} component={Holiday} />
                </Switch>
                <Refocuser />
              </EmailInterceptorProvider>
            </Router>
          </QueryClientProvider>
        </CustomerContextProvider>
      </div>
    </Elements>
  );
};

export default CustomerApp;
